// Shared shape-handling for the Run Details surfaces (Agent UI History,
// Company Admin usage drill-in, Platform Admin usage drill-in). All three read
// the same `detail` envelope, so the unwrapping/labelling rules live here and
// can't drift between them.
//
// Number/duration/cost formatting is deliberately NOT duplicated — it is
// imported from ../UsageAnalytics/usageFormat so a run row renders identically
// in the usage table and in this drawer.

/**
 * `GET …/runs/{id}/detail` answers `{ "detail": { … } }`; the admin variant has
 * been seen to answer the object directly. Accept both rather than coupling the
 * UI to one surface's envelope.
 */
export function unwrapRunDetail(payload) {
  if (!payload) return null;
  if (payload.detail && typeof payload.detail === 'object') return payload.detail;
  // Already unwrapped if it carries any of the trace's own fields.
  if (payload.run || payload.input_text !== undefined || payload.tool_calls) return payload;
  return null;
}

/** Absolute local timestamp — run history is audit-ish, so never "2h ago" alone. */
export function formatWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}

/** Compact relative age used as a secondary line next to the absolute time. */
export function formatAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const secs = Math.round((Date.now() - d.getTime()) / 1000);
  if (secs < 0) return '';
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
}

/** Deleted agents keep their history: `agent_id` goes null, `agent_name` stays. */
export function runTitle(run) {
  if (!run) return 'Run';
  return (
    run.agent_name ||
    run.chain_name ||
    run.team_name ||
    run.model_label ||
    (run.agent_id != null ? `Agent #${run.agent_id}` : null) ||
    (run.run_id ? `Run ${String(run.run_id).slice(0, 8)}…` : `Run #${run.id ?? ''}`)
  );
}

/** A run row's status, whether the API spelled it `success` or `status`. */
export function runStatus(run) {
  if (!run) return 'unknown';
  if (run.success === true) return 'success';
  if (run.success === false) return 'error';
  if (run.status) return String(run.status);
  return 'unknown';
}

/**
 * Tool `args`/`result` arrive as strings that usually hold JSON. Pretty-print
 * when they parse, otherwise hand back the raw text untouched — a truncated
 * value (see the `truncated` flag) will never parse and must still be readable.
 */
export function prettyMaybeJson(value) {
  if (value == null) return '';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  const text = String(value);
  const trimmed = text.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return text;
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2);
  } catch {
    return text;
  }
}

/**
 * §8 error table: 404 covers "not found" AND "outside your scope" (another
 * tenant's run is never 403), 400 carries a human-written `detail`, 500 usually
 * means the trace migration hasn't been applied yet.
 */
export function runDetailErrorMessage(err) {
  const status = err?.status;
  const detail = err?.data?.detail;
  if (status === 404) return 'Run not found.';
  if (status === 403) return "You don't have access to this run.";
  if (status === 400 && detail) return String(detail);
  if (status === 500) return 'Something went wrong loading this run. Try again in a moment.';
  return (typeof detail === 'string' && detail) || err?.message || 'Failed to load run details.';
}

export const STEP_KIND_LABELS = {
  team_member: 'Team member',
  member: 'Team member',
  batch_loop: 'Batch item',
  loop: 'Loop iteration',
  node: 'Chain node',
};

export const TOOL_STATUS_LABELS = { ok: 'ok', error: 'error' };
